import React, { useEffect, useState } from 'react';

import { Link, styled } from '@asicsdigital/oneasics-blocks';
import algoliasearch from 'algoliasearch/lite';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Configure, Index, InstantSearch } from 'react-instantsearch-dom';
import { useLocation, useParams } from 'react-router-dom';
import { useTracking } from 'react-tracking';

import Hit from '../../components/hit/Hit';
import Hits from '../../components/hits/Hits';
import { contentfulApi } from '../../services/api';
import { algoliaAPIConfig } from '../../utils/configs';
import { setPreviewFlag, getLocale } from '../../utils/utils';

const StyledResults = styled('div', {
  fontFamily: '$asics', // asics3.0 override
  marginLeft: 'auto',
  marginRight: 'auto',

  button: {
    fontWeight: '$bold', // asics3.0 override

    // unset sfcc styles
    backgroundColor: '$primary',
    borderColor: 'unset',
    border: 0,
    display: 'block',
    lineHeight: 'normal',
    padding: '16px 15px',
    textTransform: 'unset',
    transition: 'none',

    '&:hover:not([disabled])': {
      backgroundColor: '$primaryHover',
      border: 0,
    },
  },
});

const ResultsBanner = styled('div', {
  alignItems: 'center',
  background: '$secondaryLightIndigo',
  display: 'flex',
  justifyContent: 'center',
  height: '40px',
  left: 0,
  width: '100%',
});



const ResultsBody = styled('div', {
  margin: '0',
  width: '100%',
  '@tablet' : {
    margin: '0 auto 0 auto',
    maxWidth: '1024px',
    width: 'calc(100% - 60px)',
    padding: '0 30px',
  },
});

const Header = styled('div', {
  marginBottom: '10px',
  marginTop: '20px',
  div: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'space-between',
    width: '85%',
  },
  h1: {
    color: '$textPrimary',
    fontSize: '$large',
    fontWeight: '$bold',
    lineHeight: '1.2em',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    width: '260px',
  },
  a: {
    fontSize: '15px',
    lineHeight: '1.3em',
    fontWeight: '$regular',
  },
  '@tablet': {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    marginBottom: '50px',
    marginTop: '30px',
    a: {
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    div: {
      display: 'flex',
      margin: 0,
      width: '300px',
    },
    h1: {
      color: '$textPrimary',
      marginLeft: 'unset',
      marginRight: 'unset',
      marginTop: 0,
      textAlign: 'left',
      width: '500px',
      padding: '0px',
    },
  },
  
});

const SectionHeader = styled('div', {
  marginTop: '35px',
  marginBottom: '35px',
  div: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'space-between',
    width: '85%',
  },

  h1: {
    color: '$textPrimary',
    fontSize: '$large',
    fontWeight: '$semiBold',
    marginBottom: '25px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '25px',
    textAlign: 'center',
    width: '260px',
  },

  '@tablet': {
    h1: {
      color: '$textPrimary',
      marginLeft: 'unset',
      marginRight: 'unset',
      marginTop: 0,
      textAlign: 'left',
      width: '500px',
    },
  },
});


function dedupe(hits1, hits2) {
  const hits1Set = hits1.reduce((a, c) => a.add(c.masterId), new Set());
  const hits2Set = hits2.filter((v) => !hits1Set.has(v.masterId));
  return hits2Set;
}

/**
 * The Shoefinder results page.
 */
const ShoeFinderResults = ({ sfccData }) => {
  // const intl = useIntl();
  // console.log(intl.locale); eventually check locale
  const [showHeader, setShowHeader] = useState(false);
  const [showCloseMatchHeader, setCloseMatchHeader] = useState(false);
  const [numberOfHits, setNumberOfHits] = useState(0);
  const locales = useParams();
  const { trackEvent } = useTracking();

  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const searchFilter = urlSearchParams.get('filters');

  const gender = urlSearchParams.get('gender');
  const closeMatchFilter = urlSearchParams.get('closeMatch');
  const optFilters = urlSearchParams.get('optionalFilters');
  const numberOfResults = urlSearchParams.get('numberOfResults') || 3;
  const resultsPageId = urlSearchParams.get('resultsPageId') || '3xeBwFgthDrnUNJOHYEHHf';
  const ruleContexts = urlSearchParams.get('ruleContexts')?.split(',') || [];
  const exactMatch = `${gender} AND ${searchFilter}`;
  const indexName = urlSearchParams.get('index');
  const previewFlag = setPreviewFlag(sfccData);
  const [pollingInterval, setPollingInterval] = useState(0);
  useEffect(() => {
    if(previewFlag) {
      setPollingInterval(500);
    } else {
      setPollingInterval(0);
    }
  }, [pollingInterval]);

  useEffect(() => {
    const scrollToView = () => {
      if (document.getElementById('primary') !== null){
        document.getElementById('primary').scrollIntoView();
      }
    };
    document.addEventListener('DOMContentLoaded', scrollToView);
    // Remove the event listener when component unmounts
    return () => document.removeEventListener('DOMContentLoaded', scrollToView);

  }, []);

  let algoliaClient;
  if (indexName?.includes('bbtn-0')) {
    algoliaClient = algoliasearch(algoliaAPIConfig.algoliaAppId_Sandbox, algoliaAPIConfig.algoliaKey_Sandbox);
  }
  else {
    algoliaClient = algoliasearch(algoliaAPIConfig.algoliaAppId, algoliaAPIConfig.algoliaKey);
  }

  const closeMatch = `(${gender}) AND (${closeMatchFilter})`;

  const trackClick = (gaEventAction, label) => {
    trackEvent({
      gaEventAction,
      gaEventLabel: label,
      gaEventNonInteraction: false,
      sf2_section_label: 'results',
    });
  };

  const getFormattedOptionalFilters = (options) => {
    if(options) {
      const tempData = options.split(':');
      const score = options.split('=')[1]?.substring(0, 1);
      const key = tempData[0];
      const tempValues = tempData[1].split('<');
      const values = tempValues[0].split(',');
  
      let formattedOptFilter = [];
      values.forEach((ele) => {
        const optionalWeight  = score ? `<score=${score}>` : '';
         formattedOptFilter = formattedOptFilter.concat(`${key}:${ele}${optionalWeight}`);
      });
      return formattedOptFilter;
    }
    return [];
  };

  const formattedOptionalFilters = getFormattedOptionalFilters(optFilters);

  const searchClient = {
    ...algoliaClient,
    search(requests) {
      return algoliaClient.search(requests).then((response) => {
        const lastResultSet = response.results[0];
        setShowHeader(true);
        setNumberOfHits(lastResultSet?.hits.length);
        // setNumberOfFiltersMatched(lastResultSet?.hits?.[0]?._rankingInfo?.filters);

        // Remove duplicates
        const exactHits = response.results[0].hits;
        const closeHits = response.results[1].hits;
        response.results[1].hits = dedupe(exactHits, closeHits);
        
        // Limit output to only configured number in CMS.
        if(exactHits.length === numberOfResults) {
          response.results[1].hits = [];
        } else {
          const diff = numberOfResults - exactHits.length;
          const temp = response.results[1].hits.slice(0, diff);
          response.results[1].hits = temp;
        }

        if(response.results[1]?.hits.length > 0) {
          setCloseMatchHeader(true);
        }
        return response;
      });
    },
  };

  // Ccreate Result data object
  const resultData = {
    resultsPageId,
    locale: getLocale(locales),
    previewFlag,
  };


  const { data } = contentfulApi.useGetResultsPageQuery(resultData, { pollingInterval });
  const resultsPageData = data?.data?.resultsPage;
  // const ogImage = resultsPageData?.ogImage?.url ?? 'https://images.ctfassets.net/q9nezw9z42jm/41irMR326sqZiwir6ce8VF/3dcf00e0e36f4b99e647cc5eed118bbc/asics-running-shoe-finder__2_.png';
  const ogTag1 = resultsPageData?.ogTag1 ?? '';
  let url = 'http://www.asics.com/us/en-us/shoefinder';

  if (typeof window !== 'undefined') {
    url = window?.location?.href;
  }

  let headerText = '';
  if (numberOfHits === 0) {
    headerText = resultsPageData?.noResultsErrorText;
  } else {
    headerText = resultsPageData?.notPerfectMatchText;
  }

  const onRestart = (name) => {
    trackClick(`${name}-link-click`, name);
  };

  const onEdit = (name) => {
    trackClick(`${name}-link-click`, name);
  };

  return (
    <StyledResults>
      {resultsPageData && <Helmet>
        <meta content={ogTag1} property="og:tag1"  />
        {resultsPageData?.hidePageFromSearchEngines && <meta content="noindex" name="robots"  />}
        {resultsPageData?.excludeLinksFromSearchEngines && <meta content="nofollow" name="robots"  />}
      </Helmet>}
      <Helmet>
        {/* <meta content={ogImage} data-react-helmet="true" property="og:image"  />
        <meta content={ogImage} data-react-helmet="true" property="twitter:image"  /> */}
        <meta content="summary_large_image" data-react-helmet="true" property="twitter:card" />
        <meta content={url} data-react-helmet="true" property="twitter:url"  />
      </Helmet>

      <ResultsBanner>
        <img alt={resultsPageData?.logo?.title} src={resultsPageData?.logo?.url} />
      </ResultsBanner>
      <ResultsBody>
        {showHeader && (
          <Header>
            <div>
              <Link // eslint-disable-line jsx-a11y/anchor-is-valid
                id="edit-answers"
                linkType="external"
                name="edit-answers"
                path={`${sfccData?.wizardUrl}/?noFetch=true`}
                onClick={() => {
                  onEdit('edit-answers');
                }}
              >
                {resultsPageData?.editAnswersText}
              </Link>
              <Link // eslint-disable-line jsx-a11y/anchor-is-valid
                id="restart-quiz"
                linkType="external"
                name="restart-quiz"
                path={sfccData?.wizardUrl}
                onClick={() => {
                  onRestart('restart-quiz');
                }}
              >
                {resultsPageData?.restartQuizText}
              </Link>
            </div>
            <h1>{headerText}</h1>
          </Header>
        )}
        
        <InstantSearch indexName={indexName} searchClient={searchClient} >
          <Configure
            distinct
            explain="*"
            filters={exactMatch}
            hitsPerPage={numberOfResults}
            optionalFilters={formattedOptionalFilters}
            ruleContexts={ruleContexts}
          />
          <Hits hitComponent={Hit} locale={getLocale(locales)} resultsPageData={resultsPageData} urlSearchParams={urlSearchParams} />

          {showCloseMatchHeader && (<SectionHeader>
            <h1>{resultsPageData?.closeMatchText}</h1>
          </SectionHeader>)}

          <Index indexId="close" indexName={indexName}>
            <Configure
              distinct
              sumOrFiltersScores
              explain="*"
              filters={closeMatch}
              hitsPerPage={numberOfResults}
              ruleContexts={ruleContexts}
            />
            <Hits hitComponent={Hit} locale={getLocale(locales)} resultsPageData={resultsPageData} urlSearchParams={urlSearchParams} />
          </Index>
        </InstantSearch>
      </ResultsBody>
    </StyledResults>
  );
};

ShoeFinderResults.propTypes = {
  sfccData: PropTypes.shape({
    component: PropTypes.string,
    contentfulWizardId: PropTypes.string,
    resultsUrl: PropTypes.string,
    wizardUrl: PropTypes.string,
  }),
};

ShoeFinderResults.defaultProps = {
  sfccData: {},
};

export default ShoeFinderResults;
